
import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import {
  Document,
  ExternalValidationResults,
} from "../../../../domain/document";
import { Patient } from "../../../../domain/patient";
import {
  RestrictedDocumentHandleableMixin,
  SpecifiedDocumentHandleableMixin,
} from "../../mixins";
import { EditingDocument } from "../../editor";
import {
  DocumentEditorBaseMixin,
} from ".";
import { DomainRegistry } from "../../../../domain";

@Component({
})
export default class ExistingDocumentEditorMixin extends Mixins(SpecifiedDocumentHandleableMixin, DocumentEditorBaseMixin, RestrictedDocumentHandleableMixin) {
  @Prop()
  load: (patientNo: string, documentId: number, editToken?: string, writeToken?: string) => Promise<Document>;

  @Prop()
  cancel: (source: Document) => Promise<void>;
  
  @Prop({ default: null })
  initialize: (source: Document) => Promise<void>;

  @Prop()
  redirectUneditableDocument: (source: Document) => Promise<void>;

  @Prop()
  applyExternalRequestObjectValues: (document: EditingDocument) => Promise<void>;

  @Prop()
  validateWithExternalValidationUrl: () => Promise<ExternalValidationResults>;

  @Prop()
  chainedFormatIds: number[];

  async initializeDocument(source: Document): Promise<void> {
    if (this.initialize) {
      await this.initialize(source);
    }
  }

  created(): void {
    this.loadExistingDocument();
  }

  @Watch("patient")
  onPatientUpdated(newValue: Patient, oldValue: Patient): void {
    this.loadExistingDocument();
  }

  isLoadRequested: boolean = false;
  isReadRestrictionAlreadyConfirmed: boolean = false;
  isScannedDocumentAlreadyConfirmed: boolean = false;

  private async loadExistingDocument(editToken?: string, writeToken?: string): Promise<void> {
    const patientNo = this.patient
      ? this.patient.id
      : this.patientNo;
    if (!patientNo || !this.documentId || this.isLoadRequested) {
      return;
    }
    this.isLoadRequested = true;

    // 編集開始してよいかのチェック
    const isEditable = await this.isDocumentEditable(patientNo);
    if (!isEditable) { return; }

    try {
      const document = await this.load(patientNo, this.documentId, editToken, writeToken);
      if (document) {
        document.chainedFormatOriginalIds = this.chainedFormatIds;
        document.freezeItems();
      }
      this.editingSource = document;
    } catch (e) {
      this.$fatal(
        "文書読込失敗",
        "指定された患者番号(patientNo)と文書ID(documentId)の編集文書読込失敗しました。",
        e
      );
    }
  }

  private async isDocumentEditable(patientNo: string): Promise<boolean> {
    try {
      const isReadable = await this.handleReadRestriction(patientNo, "中止して画面を閉じる");
      if (!isReadable) {
        this.$closeWindow(true);
        return false;
      }
      if (this.$configuration.behavior.collaboration.scan.shouldVerifyOnEditStart) {
        const isEditable = !(await this.isScannedDocument(patientNo, this.documentId)) || (await this.confirmToEditScannedDocument()); 
        if (!isEditable) {
          this.$closeWindow(true);
          return false;
        }
      }
      return true;
    } catch (e) {
      this.$fatal(
        "文書情報取得失敗",
        "文書情報の取得に失敗しました。",
        e
      );
    }
    return false;
  }

  private async isScannedDocument(patientNo: string, documentId: number): Promise<boolean> {
    const repository = DomainRegistry.collaboration.getCollaborationRepository();
    return repository.isScanned(patientNo, documentId);
  }

  private async confirmToEditScannedDocument(): Promise<boolean> {
    if (this.isScannedDocumentAlreadyConfirmed) { return true; }
    try {
      const index = await this.$commonDialogs.confirmation.confirmDangerCancel(
        "編集の確認",
        [this.$configuration.behavior.collaboration.scan.scannedDocumentEditConfirmMessage],
        "編集を開始する"
      );
      const isConfirmed = (index === 0);
      if (isConfirmed) {
        this.isScannedDocumentAlreadyConfirmed = true;
      }
      return isConfirmed;
    } catch (e) {
      return false;
    }
  }

  private async handleReadRestriction(patientNo: string, cancelButtonText: string): Promise<boolean> {
    // 編集中の競合を突破したときは読み込み直しされるので、すでに処理済みの場合はスキップする
    if (this.isReadRestrictionAlreadyConfirmed) { return true; }
    const isConfirmed = await this.isReadConfirmedDocument(patientNo, this.documentId, cancelButtonText);
    this.isReadRestrictionAlreadyConfirmed = isConfirmed;
    return isConfirmed;
  }

  reloadDocument(editToken: string, writeToken: string): void {
    this.isLoadRequested = false;
    this.loadExistingDocument(editToken, writeToken);
  }
}
